import { useRef, useState } from "react";
import { Camera, Play } from "lucide-react";
import PageWrapper from "@/components/layout/PageWrapper";
import Breadcrumb from "@/components/Breadcrumb";
import { useLanguage } from "@/i18n/LanguageContext";

const galleryItems = [
  { type: "image", src: "/gallery-1.jpeg", label: "Our campus & play area", span: "row-span-2" },
  { type: "image", src: "/gallery-2.jpeg", label: "Outdoor activities", span: "" },
  { type: "image", src: "/gallery-3.jpeg", label: "Classroom learning", span: "" },
  { type: "image", src: "/gallery-4.jpeg", label: "Our students & teachers", span: "row-span-2" },
  { type: "image", src: "/gallery-5.jpeg", label: "Group activities", span: "" },
  { type: "image", src: "/gallery-6.jpeg", label: "Republic Day celebration", span: "" },
  { type: "image", src: "/gallery-7.jpeg", label: "Our school family", span: "" },
];

const galleryVideos = [
  { src: "/gallery-v1.mp4", label: "School moments" },
  { src: "/gallery-v2.mp4", label: "Activities in action" },
  { src: "/gallery-v3.mp4", label: "Fun & learning" },
  { src: "/gallery-v4.mp4", label: "Celebration day" },
  { src: "/gallery-v5.mp4", label: "Classroom fun" },
  { src: "/gallery-v6.mp4", label: "Annual function" },
  { src: "/gallery-v7.mp4", label: "Playtime joy" },
  { src: "/gallery-v8.mp4", label: "School highlights" },
];

const VideoTile = ({ src, label, testId }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  return (
    <figure
      data-testid={testId}
      className="relative overflow-hidden rounded-2xl group bg-black"
    >
      <video
        ref={ref}
        src={`${src}#t=0.5`}
        playsInline
        preload="metadata"
        controls={playing}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="h-full w-full object-contain aspect-[4/3] bg-black"
      />
      {!playing && (
        <button
          type="button"
          aria-label={`Play video: ${label}`}
          data-testid={`${testId}-play`}
          onClick={() => ref.current && ref.current.play()}
          className="absolute inset-0 flex items-center justify-center bg-black/15 transition-colors hover:bg-black/30 cursor-pointer"
        >
          <span className="grid place-items-center h-14 w-14 rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110">
            <Play size={24} className="ml-0.5 text-[color:var(--adhd-primary)]" fill="currentColor" />
          </span>
        </button>
      )}
      <figcaption className="absolute inset-x-0 top-0 p-3 text-xs sm:text-sm text-white font-semibold bg-gradient-to-b from-black/55 to-transparent pointer-events-none">
        {label}
      </figcaption>
    </figure>
  );
};

const Gallery = () => {
  const { t } = useLanguage();
  return (
    <PageWrapper
      title="Gallery — ADHD School Bhadrak"
      description="Photo gallery of Aachary Dev Development Holistic School Bhadrak — classrooms, therapy sessions and events."
    >
      <section className="adhd-container pt-10 lg:pt-14">
        <Breadcrumb pageKey="nav.gallery" />
        <p className="adhd-eyebrow mt-6">
          <Camera size={13} /> Moments
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-[color:var(--adhd-text)] max-w-2xl">
          {t("gallery.title")}
        </h1>
        <p className="mt-3 text-[color:var(--adhd-muted)] max-w-2xl">{t("gallery.subtitle")}</p>
      </section>

      <section className="adhd-container mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {galleryItems.map((g, i) => (
          <figure
            key={i}
            data-testid={`gallery-item-${i}`}
            className={`relative overflow-hidden rounded-2xl group ${g.span || ""} ${i % 5 === 0 ? "col-span-2" : ""}`}
          >
            <img
              src={g.src}
              alt={g.label}
              loading="lazy"
              className="h-full w-full object-cover aspect-[4/3] transition-transform duration-700 group-hover:scale-105"
            />
            <figcaption className="absolute inset-x-0 bottom-0 p-3 text-xs sm:text-sm text-white font-semibold bg-gradient-to-t from-black/55 to-transparent">
              {g.label}
            </figcaption>
          </figure>
        ))}
      </section>

      <section className="adhd-container mt-12 mb-10">
        <p className="adhd-eyebrow">
          <Play size={13} /> Videos
        </p>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl leading-tight text-[color:var(--adhd-text)] max-w-2xl">
          Watch Our Moments
        </h2>
        <p className="mt-2 text-[color:var(--adhd-muted)] max-w-2xl">
          Tap any video to play it right here.
        </p>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {galleryVideos.map((v, i) => (
            <VideoTile key={i} src={v.src} label={v.label} testId={`gallery-video-${i}`} />
          ))}
        </div>
      </section>
    </PageWrapper>
  );
};

export default Gallery;
